import { skipToken } from "@apollo/client";
import { useSuspenseQueryHandler } from "@sinch/hooks/useSuspenseQueryHandler";
import React, { PropsWithChildren, useCallback, useContext, useTransition } from "react";
import { RateList } from "@sinch/components/application/RatelistBuilder/queries.graphql";
import { RateListQuery, RateListQueryVariables } from "@sinch/components/application/RatelistBuilder/queries.types";

type RatelistDataQuery = ReturnType<typeof useSuspenseQueryHandler<RateListQuery, RateListQueryVariables>>;

export type RatelistData = RatelistDataQuery & {
  /**
   * `true` while a `refetch` transition is still in flight. The previously loaded data stays on screen
   * meanwhile, so use this for inline loading indicators instead of expecting a Suspense fallback.
   */
  refetching: boolean;
};

export const RatelistDataQueryContext = React.createContext<RatelistData | undefined>(undefined);

export function RatelistDataProvider({ children, ratelistId }: PropsWithChildren<{ ratelistId?: string | null }>) {
  const { refetch: refetchQuery, ...queryResult } = useSuspenseQueryHandler<RateListQuery, RateListQueryVariables>(
    RateList,
    { id: ratelistId as string },
    ratelistId ? { skip: !ratelistId } : skipToken
  );
  const [refetching, startTransition] = useTransition();

  /**
   * `useSuspenseQuery` re-suspends on `refetch`, so calling it directly makes the Suspense boundary above the
   * builder swap the entire subtree for its fallback and reveal it again once the data arrives. React re-runs
   * mount lifecycles on that reveal while refs are still detached, which crashes any MUI transition open at
   * that moment (`Grow` calling `reflow()` on a null node). Refetching inside a transition keeps the current
   * content mounted instead of showing the fallback.
   */
  const refetch = useCallback<RatelistDataQuery["refetch"]>(
    (variables) => {
      let promise: ReturnType<RatelistDataQuery["refetch"]> | undefined;
      // `startTransition` invokes its callback synchronously, so the promise is always assigned before returning.
      startTransition(() => {
        promise = refetchQuery(variables);
      });
      return promise!;
    },
    [refetchQuery]
  );

  return (
    <RatelistDataQueryContext.Provider value={{ ...queryResult, refetch, refetching }}>
      {children}
    </RatelistDataQueryContext.Provider>
  );
}

export function useRatelistData() {
  const context = useContext(RatelistDataQueryContext);
  if (!context) {
    throw new Error("useRatelistData must be used within RatelistDataQueryContext");
  }
  return context;
}
